/**
 * 管理配置分区（i18n 命名空间思路，2026-08-15）
 *
 * 独立文件维护：系统设置页「配置组卡片」的文案（按钮/字段标签/说明 hint）。
 * 与 tool.ts 同模式：translations.ts 运行时合并；key 在 adminConfig 分区内是**裸名**
 * （'save' / 'embeddingLabel'），前端用 t('adminConfig:save') 取，
 * ConfigGroupCard 的 tr() 已统一补 'adminConfig:' 前缀——不要再写 'configGroup.' 之类的重复前缀。
 * 后端 CONFIG_GROUPS schema 只下发 label_key/hint_key（同样是裸名），前端 t() 翻译。
 */

import type { TranslationDict } from '../types'

export const adminConfigZh: TranslationDict = {
  // ── 配置组通用 UI ──
  'sourceDb': '界面已修改',
  'sourceEnv': '环境变量',
  'secretSet': '（已设置，留空不修改）',
  'secretNotSet': '（未设置）',
  'secretPlaceholder': '••••••••（已加密存储）',
  'save': '保存并生效',
  'saved': '已保存',
  'reset': '恢复默认',
  'resetConfirm': '确定恢复「{label}」的默认值？将清除界面修改，回到环境变量配置。',
  'resetDone': '已恢复默认（回到环境变量配置）',
  'saveFailed': '保存失败',
  'resetFailed': '恢复失败',
  'test': '测试连接',
  'testing': '测试中...',
  'testOk': '连接成功（维度 {dim}）',
  'testFail': '{msg}',

  // ── embedding 配置组 ──
  'embeddingLabel': 'Embedding 向量配置',
  'embeddingHint': '控制 AI 记忆的「语义搜索」能力。改这里不需要懂代码——选一个后端、填地址、保存即可。',
  'embeddingBackend': '后端类型',
  'embeddingBackendHint': '选择用谁来生成「向量」（记忆搜索的钥匙）。不启用 = 只用文字匹配，记忆功能正常但搜得没那么准。',
  'backendDisabled': '不启用（纯文本检索）',
  'backendOllama': 'Ollama（本地）',
  'backendApi': 'OpenAI 兼容 API',
  'backendLocal': '本地模型（fastembed）',
  'embeddingBaseUrl': 'API 地址 (Base URL)',
  'embeddingBaseUrlHint': '向量服务在哪。容器内访问宿主机 Ollama 填 http://host.docker.internal:11434；Ollama 若是另一个 compose 服务填服务名；后端不在容器里跑填 http://127.0.0.1:11434；用厂商 API 填 https://地址/v1。',
  'embeddingApiKey': 'API Key',
  'embeddingApiKeyHint': '只有用「OpenAI 兼容 API」才需要。Ollama 和本地模型不用填。密钥加密保存，不会明文显示。',
  'embeddingModel': '模型',
  'embeddingModelHint': '具体用哪个模型。Ollama 常用 nomic-embed-text；不知道填什么就留空用默认。',
  'embeddingDimension': '向量维度',
  'embeddingDimensionHint': '数字越大搜得越准、越占空间；越小越快越省。需与模型匹配（nomic-embed-text=768）。改这里一般不用动。',

  // ── runtime 配置组 ──
  'runtimeLabel': '运行时参数',
  'runtimeHint': '调整 AI 检索记忆和展示时间的细节。这些是「锦上添花」的选项，不调也完全能用。',
  'topK': '默认检索条数 (top_k)',
  'topKHint': 'AI 每次从记忆里翻出几条来参考。条数多 = 参考更全面但更费 token；少 = 更快更省。',
  'vectorWeight': '向量权重',
  'vectorWeightHint': '检索时「意思相近」的结果占多大比重。调高 = 更看重语义相似。',
  'bm25Weight': 'BM25 权重',
  'bm25WeightHint': '检索时「字面相同」的结果占多大比重。调高 = 更看重关键词完全一致。',
  'timeDecayWeight': '时间衰减权重',
  'timeDecayWeightHint': '最近的记忆占多大比重。调高 = 更优先最近的事。',
  'displayTimezone': '显示时区',
  'displayTimezoneHint': 'AI 看到的时间用哪个时区，如 Asia/Shanghai（中国）或 UTC。',
  'summaryCacheTtl': '摘要缓存 TTL（秒）',
  'summaryCacheTtlHint': '对话摘要缓存多久。600 = 10 分钟。调大省 token，调小更实时。',
  'uploadMaxSize': '单文件上传大小上限（MB）',
  'uploadMaxSizeHint': '用户上传单个文件的上限，不含头像。落 runtime 配置组，重启不丢。',
  'avatarMaxSize': '头像上传大小上限（MB）',
  'avatarMaxSizeHint': '用户 / AI 上传头像的上限。同样落 runtime 配置组。',
}

export const adminConfigEn: TranslationDict = {
  'sourceDb': 'Edited in UI',
  'sourceEnv': 'Env var',
  'secretSet': ' (set, leave blank to keep)',
  'secretNotSet': ' (not set)',
  'secretPlaceholder': '•••••••• (encrypted)',
  'save': 'Save & Apply',
  'saved': 'Saved',
  'reset': 'Reset',
  'resetConfirm': 'Reset "{label}" to defaults? This clears UI overrides and falls back to env vars.',
  'resetDone': 'Reset to defaults (env vars)',
  'saveFailed': 'Save failed',
  'resetFailed': 'Reset failed',
  'test': 'Test Connection',
  'testing': 'Testing...',
  'testOk': 'Connected (dim {dim})',
  'testFail': '{msg}',

  'embeddingLabel': 'Embedding Config',
  'embeddingHint': 'Controls AI memory "semantic search". No coding needed — pick a backend, fill the address, save.',
  'embeddingBackend': 'Backend Type',
  'embeddingBackendHint': 'Who generates "vectors" (the key for memory search). Disabled = text-only matching; memory still works but less precise.',
  'backendDisabled': 'Disabled (text search)',
  'backendOllama': 'Ollama (local)',
  'backendApi': 'OpenAI-compatible API',
  'backendLocal': 'Local model (fastembed)',
  'embeddingBaseUrl': 'API Base URL',
  'embeddingBaseUrlHint': 'Where the embedding service lives. From inside a container to host Ollama: http://host.docker.internal:11434; Ollama as another compose service: its service name; backend outside Docker: http://127.0.0.1:11434; API: https://host/v1.',
  'embeddingApiKey': 'API Key',
  'embeddingApiKeyHint': 'Only needed for "OpenAI-compatible API". Not needed for Ollama or local models. Stored encrypted.',
  'embeddingModel': 'Model',
  'embeddingModelHint': 'Which model to use. Ollama usually nomic-embed-text; leave blank for default if unsure.',
  'embeddingDimension': 'Vector Dimension',
  'embeddingDimensionHint': 'Higher = more accurate but more storage; lower = faster and leaner. Must match model (nomic-embed-text=768). Usually no need to change.',

  'runtimeLabel': 'Runtime Parameters',
  'runtimeHint': 'Tune how AI retrieves memories and displays time. These are "nice-to-have" — defaults work fine.',
  'topK': 'Default top_k',
  'topKHint': 'How many memories AI pulls for reference. More = broader but more tokens; fewer = faster and cheaper.',
  'vectorWeight': 'Vector Weight',
  'vectorWeightHint': 'Weight of "semantically similar" results. Higher = favors meaning.',
  'bm25Weight': 'BM25 Weight',
  'bm25WeightHint': 'Weight of "exact keyword" results. Higher = favors literal matches.',
  'timeDecayWeight': 'Time Decay Weight',
  'timeDecayWeightHint': 'Weight of recency. Higher = favors recent memories.',
  'displayTimezone': 'Display Timezone',
  'displayTimezoneHint': 'Timezone AI sees, e.g. Asia/Shanghai or UTC.',
  'summaryCacheTtl': 'Summary Cache TTL (s)',
  'summaryCacheTtlHint': 'How long conversation summaries are cached. 600 = 10 min. Higher saves tokens, lower is more realtime.',
  'uploadMaxSize': 'Max upload size per file (MB)',
  'uploadMaxSizeHint': 'Per-file upload limit for users, avatars excluded. Stored in the runtime config group, survives restarts.',
  'avatarMaxSize': 'Max avatar upload size (MB)',
  'avatarMaxSizeHint': 'Avatar upload limit for users and AIs. Also stored in the runtime config group.',
}

export const adminConfigJa: TranslationDict = {
  'sourceDb': 'UI で変更済み',
  'sourceEnv': '環境変数',
  'secretSet': '（設定済み・空欄で変更なし）',
  'secretNotSet': '（未設定）',
  'secretPlaceholder': '••••••••（暗号化済み）',
  'save': '保存して適用',
  'saved': '保存済み',
  'reset': 'リセット',
  'resetConfirm': '「{label}」を初期値に戻しますか？UI での変更を消して環境変数に戻します。',
  'resetDone': '初期値に戻しました（環境変数）',
  'saveFailed': '保存に失敗',
  'resetFailed': 'リセットに失敗',
  'test': '接続テスト',
  'testing': 'テスト中...',
  'testOk': '接続成功（次元 {dim}）',
  'testFail': '{msg}',

  'embeddingLabel': 'Embedding ベクトル設定',
  'embeddingHint': 'AI メモリーの「意味検索」を制御します。コード不要——バックエンドを選んで、アドレスを入れて、保存するだけ。',
  'embeddingBackend': 'バックエンド種別',
  'embeddingBackendHint': '「ベクトル」（メモリー検索の鍵）を生成する人。無効 = テキスト一致のみ、機能は動くが精度は落ちます。',
  'backendDisabled': '無効（テキスト検索）',
  'backendOllama': 'Ollama（ローカル）',
  'backendApi': 'OpenAI 互換 API',
  'backendLocal': 'ローカルモデル（fastembed）',
  'embeddingBaseUrl': 'API アドレス (Base URL)',
  'embeddingBaseUrlHint': 'ベクトルサービスの場所。コンテナからホストの Ollama は http://host.docker.internal:11434、別の compose サービスならサービス名、コンテナ外なら http://127.0.0.1:11434。API は https://host/v1。',
  'embeddingApiKey': 'API Key',
  'embeddingApiKeyHint': '「OpenAI 互換 API」のときだけ必要。Ollama・ローカルモデルは不要。暗号化して保存されます。',
  'embeddingModel': 'モデル',
  'embeddingModelHint': '使うモデル。Ollama は通常 nomic-embed-text。分からなければ空欄でデフォルト。',
  'embeddingDimension': 'ベクトル次元',
  'embeddingDimensionHint': '大きいほど正確・容量大、小さいほど高速・省スペース。モデルと一致させる（nomic-embed-text=768）。通常変更不要。',

  'runtimeLabel': '実行時パラメータ',
  'runtimeHint': 'メモリー検索と時刻表示の細部を調整。なくても問題なく動きます。',
  'topK': 'デフォルト top_k',
  'topKHint': 'AI が参照するメモリー数。多い = 広いがトークン消費大。少ない = 高速・節約。',
  'vectorWeight': 'ベクトル重み',
  'vectorWeightHint': '「意味が近い」結果の比率。高く = 意味重視。',
  'bm25Weight': 'BM25 重み',
  'bm25WeightHint': '「文字が一致」する結果の比率。高く = キーワード完全一致重視。',
  'timeDecayWeight': '時間減衰の重み',
  'timeDecayWeightHint': '最近のメモリーの比率。高く = 最近の出来事を優先。',
  'displayTimezone': '表示タイムゾーン',
  'displayTimezoneHint': 'AI が見る時刻のタイムゾーン。例：Asia/Shanghai または UTC。',
  'summaryCacheTtl': '要約キャッシュ TTL（秒）',
  'summaryCacheTtlHint': '会話要約のキャッシュ時間。600 = 10 分。大きく = トークン節約、小さく = よりリアルタイム。',
  'uploadMaxSize': 'ファイル 1 件の最大サイズ（MB）',
  'uploadMaxSizeHint': 'ユーザーが 1 ファイルをアップロードできる上限（アバターを除く）。runtime 設定グループに保存され、再起動しても残ります。',
  'avatarMaxSize': 'アバターの最大サイズ（MB）',
  'avatarMaxSizeHint': 'ユーザー / AI のアバター上限。同じく runtime 設定グループに保存されます。',
}
